import React, { useEffect, useState, useCallback } from 'react'
import { Row, Col, Button, Space, Spin, Card, DatePicker } from 'antd'
import {
  DollarOutlined, UserAddOutlined, FireOutlined, CrownOutlined,
  VideoCameraOutlined, ShoppingCartOutlined, TeamOutlined, StarOutlined,
  RiseOutlined, RollbackOutlined, GiftOutlined, TrophyOutlined,
  LeftOutlined, RightOutlined,
} from '@ant-design/icons'
import { attendanceApi, home } from '../../api'
import { useAuth } from '../../store/auth'
import dayjs, { Dayjs } from 'dayjs'

const KPI_CONFIG = [
  { key: 'gmv',             title: '今日GMV',       prefix: '¥', suffix: '',  icon: <DollarOutlined />,    color: 'blue' },
  { key: 'plan_orders',     title: '方案订单数',     prefix: '',  suffix: '单', icon: <ShoppingCartOutlined />, color: 'grey' },
  { key: 'paid_users',      title: '付费用户数',     prefix: '',  suffix: '人', icon: <FireOutlined />,      color: 'pink' },
  { key: 'new_users',       title: '新增用户',       prefix: '',  suffix: '人', icon: <UserAddOutlined />,   color: 'green' },
  { key: 'return_users',    title: '回流用户',       prefix: '',  suffix: '人', icon: <RollbackOutlined />,  color: 'teal' },
  { key: 'active_big_r',   title: '大R',            prefix: '',  suffix: '人', icon: <CrownOutlined />,     color: 'violet' },
  { key: 'member_gmv',     title: '会员收入',        prefix: '¥', suffix: '',  icon: <StarOutlined />,      color: 'gold' },
  { key: 'publish_experts', title: '发单专家数',     prefix: '',  suffix: '人', icon: <TeamOutlined />,      color: 'indigo' },
  { key: 'publish_plans',   title: '发布方案量',     prefix: '',  suffix: '个', icon: <RiseOutlined />,      color: 'cyan' },
  { key: 'reserve_experts', title: '预约直播专家数', prefix: '',  suffix: '人', icon: <VideoCameraOutlined />, color: 'yellow' },
  { key: 'live_experts',    title: '直播专家数',     prefix: '',  suffix: '人', icon: <TrophyOutlined />,    color: 'orange' },
  { key: 'card_gmv',        title: '专家次卡收入',   prefix: '¥', suffix: '',  icon: <GiftOutlined />,      color: 'rose' },
  { key: 'reward_gmv',      title: '专家打赏收入',   prefix: '¥', suffix: '',  icon: <GiftOutlined />,      color: 'purple' },
]

const DAY_LABELS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

export default function HomePage() {
  const [kpi, setKpi] = useState<any>(null)
  const [attendance, setAttendance] = useState<any>(null)
  const [weekOffset, setWeekOffset] = useState(0)
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const { user } = useAuth()

  const week = dayjs().startOf('isoWeek').add(weekOffset, 'week').format('YYYY-MM-DD')
  const today = dayjs().format('YYYY-MM-DD')
  const isToday = selectedDate.format('YYYY-MM-DD') === today

  const fetchAttendance = useCallback((w: string) => {
    attendanceApi.get(w).then(setAttendance).catch(() => {})
  }, [])

  useEffect(() => {
    setKpi(null)
    home.kpi(selectedDate.format('YYYY-MM-DD')).then(setKpi).catch(() => {})
  }, [selectedDate])

  useEffect(() => {
    fetchAttendance(week)
  }, [week])

  return (
    <div>
      {/* 欢迎语 + 日期选择 */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#4A4A4A' }}>
            {isToday ? `Hi, ${user?.name || ''}，今日数据概览` : `数据概览 · ${selectedDate.format('MM月DD日')}`}
          </div>
          <div style={{ color: '#B9B9B9', fontSize: 13, marginTop: 4 }}>{selectedDate.format('YYYY年MM月DD日 dddd')}</div>
        </div>
        <Space size={8}>
          <Button size="small" onClick={() => setSelectedDate(dayjs().subtract(1, 'day'))}>昨天</Button>
          <DatePicker
            size="small"
            value={selectedDate}
            onChange={v => v && setSelectedDate(v)}
            allowClear={false}
            disabledDate={d => d.isBefore('2026-01-01') || d.isAfter(dayjs())}
          />
          {!isToday && (
            <Button size="small" type="primary" onClick={() => setSelectedDate(dayjs())}>今天</Button>
          )}
        </Space>
      </div>

      {/* KPI 数据概览 */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '20px 0',
        boxShadow: '0 2px 8px #E8EAFC',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      }}>
        {KPI_CONFIG.map((c, i) => {
          const val = kpi ? Number(kpi[c.key] ?? 0) : null
          const diff = kpi ? Number(kpi[`${c.key}_diff`] ?? 0) : 0
          const isUp = diff > 0
          const isDown = diff < 0
          return (
            <div key={c.key} style={{
              padding: '12px 20px',
              borderRight: (i + 1) % 6 !== 0 ? '1px solid #F0F0F0' : 'none',
              borderBottom: i < KPI_CONFIG.length - 6 ? '1px solid #F0F0F0' : 'none',
            }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>{c.title}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>
                {val !== null ? `${c.prefix}${val.toLocaleString()}${c.suffix}` : <Spin size="small" />}
              </div>
              <div style={{ fontSize: 11, marginTop: 4, color: isUp ? '#f5222d' : isDown ? '#52c41a' : '#ccc' }}>
                {isUp ? `↑ +${diff.toLocaleString()}` : isDown ? `↓ ${diff.toLocaleString()}` : '较昨日 —'}
              </div>
            </div>
          )
        })}
      </div>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* 全组排班/考勤矩阵 */}
        <Col span={24}>
          <Card
            title={<span style={{ fontWeight: 600 }}>📅 用户付费组本周排班</span>}
            size="small"
            style={{ borderRadius: 12 }}
            bordered={false}
            extra={
              <Space size={4}>
                <Button size="small" type="text" icon={<LeftOutlined />} onClick={() => setWeekOffset(o => o - 1)} />
                <span style={{ fontSize: 12, color: '#6E6E6E', minWidth: 140, textAlign: 'center' }}>
                  {week} ~ {dayjs(week).add(6, 'day').format('MM-DD')}
                </span>
                <Button size="small" type="text" icon={<RightOutlined />} onClick={() => setWeekOffset(o => o + 1)} />
              </Space>
            }>
            {!attendance ? <Spin /> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 560, borderCollapse: 'separate', borderSpacing: '4px 6px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6E6E6E', fontSize: 12, fontWeight: 600, width: 80 }}>成员</th>
                      {attendance.week_days.map((d: string, i: number) => {
                        const isToday = d === today
                        return (
                          <th key={d} style={{
                            textAlign: 'center', fontSize: 12, fontWeight: 600,
                            color: isToday ? '#536DFE' : '#6E6E6E', padding: '4px 0',
                          }}>
                            <div>{DAY_LABELS[i]}</div>
                            <div style={{ fontSize: 10, color: '#B9B9B9' }}>{d.slice(5)}</div>
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.members.map((m: any) => (
                      <tr key={m.name}>
                        <td style={{ padding: '4px 8px', fontSize: 13, color: '#4A4A4A', fontWeight: 500 }}>{m.name}</td>
                        {attendance.week_days.map((d: string) => {
                          const day = m.days[d]
                          const shift: string = day?.shift || '休息'
                          const isRest = shift === '休息'
                          const isToday = d === today
                          const parts = shift.split('\n')
                          return (
                            <td key={d} style={{ textAlign: 'center', padding: '2px 0' }}>
                              <div style={{
                                width: 66, minHeight: 42, borderRadius: 6, margin: '0 auto',
                                background: isRest ? '#F4F4F6' : '#EEF0FF',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                padding: '3px 2px',
                                border: isToday ? '2px solid #536DFE' : '1px solid transparent',
                              }}>
                                <span style={{
                                  fontSize: 11, fontWeight: isRest ? 400 : 600,
                                  color: isRest ? '#C0C0C0' : '#536DFE',
                                  lineHeight: 1.3, wordBreak: 'keep-all',
                                }}>{parts[0]}</span>
                                {parts[1] && (
                                  <span style={{
                                    fontSize: 9, color: isRest ? '#D0D0D0' : '#8E8ED0',
                                    lineHeight: 1.2, marginTop: 1,
                                  }}>{parts[1]}</span>
                                )}
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
